import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Living in Cancelada and Estepona with children – area guide";
const description =
  "An area guide to Cancelada, Estepona and the New Golden Mile: housing, commuting, everyday services, sports and the seasons, for families on the Costa del Sol.";

export const Route = createFileRoute("/en/living-in-cancelada-and-estepona")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/living-in-cancelada-and-estepona") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/living-in-cancelada-and-estepona"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Living in Cancelada and Estepona"
        intro="Our school sits in Cancelada, between Estepona and Marbella. It is also where many of our families live. Here is an honest area guide to everyday life: housing, distances, services, sports and how the seasons actually feel."
      />

      <NewcomerNote />

      <Prose>
        <h2>Where is Cancelada?</h2>
        <p>
          Cancelada is a small coastal town on the New Golden Mile, within the municipality of
          Estepona and just west of Marbella. The area has grown quickly in recent years but still
          keeps a village feel, with a square, bakeries, restaurants and small shops where people
          recognise each other. For a family that means something fairly rare on the coast:
          everything close by, without living in the middle of the tourist flow.
        </p>
        <p>
          From Cancelada you reach Estepona town to the west and Marbella, Puerto Banús and San
          Pedro to the east quickly. Malaga airport is usually around an hour away by car, and
          Gibraltar slightly closer in the other direction – convenient when family and friends
          visit.
        </p>

        <h2>Housing</h2>
        <p>
          The market ranges from apartments in urbanisations with shared pools to townhouses and
          villas further up towards the hills. Many Swedish families rent for the first year and
          buy – if they buy – only once they know which area suits daily life. That is a sensible
          approach: the distance between school, work and training is felt far more in reality than
          on a map.
        </p>
        <p>
          Think through whether you want to be within walking distance of the school, near the
          beach, or higher up with a view. Also check practical matters such as parking, air
          conditioning, winter heating and how community fees work. The rental market moves fast
          ahead of the school start in August and September.
        </p>

        <h2>Everyday services</h2>
        <p>
          Large supermarkets, pharmacies, a health centre, dentists, gyms and padel clubs are all
          minutes away. Estepona town offers more shops, markets and a well-kept old town of
          flower-lined lanes. Marbella and San Pedro add shopping, restaurants and specialist care.
          In practice most families need a car, even though a lot can be done on foot in Cancelada.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>Sports and the year round</h2>
        <p>
          The climate is the obvious reason many people move here: plenty of sun, mild winters and
          an outdoor season that covers most of the year. Football, padel, tennis, swimming, riding,
          golf and the sea are all nearby, which for children means activities become a natural part
          of the week rather than something you drive far for.
        </p>
        <p>
          At the same time, summers are hot, July and August are busy with visitors, and November to
          February can be rainier and cooler in the evenings than people expect. Homes built for
          heat can feel cold in January. None of that is a problem – but it is worth knowing before
          you pack your wardrobe.
        </p>

        <h2>Becoming part of the community</h2>
        <p>
          The area is international and there is a large Swedish-speaking circle. That makes
          arriving easier, but it can also become a bubble without a little effort. Our experience
          is that families who quickly find an activity – a club, a team, a padel group – settle
          faster. The school is often the first natural meeting place; read more about how we work
          with{" "}
          <Link className="text-primary link-quiet" to="/en/community">
            community and welcoming new families
          </Link>
          .
        </p>

        <h2>Checklist before the move</h2>
        <ul>
          <li>Visit the area off-season, not only on holiday.</li>
          <li>Drive from your prospective home to the school on a weekday morning.</li>
          <li>Rent before you buy – at least one school year.</li>
          <li>Plan for a car, and check parking at the property.</li>
          <li>Apply for a school place early; some year groups have limited capacity.</li>
          <li>Handle the paperwork in parallel with the move, not afterwards.</li>
        </ul>

        <p>
          If you would like to talk through your own situation before the move, we are happy to
          help. Read about our{" "}
          <Link className="text-primary link-quiet" to="/en/relocation-consultation">
            relocation consultation
          </Link>{" "}
          or{" "}
          <Link className="text-primary link-quiet" to="/en/contact-us">
            contact us
          </Link>
          .
        </p>
      </Prose>

      <KnowledgeHubNote resource="vardagen-i-spanien" locale="en" />

      <GuideLinks current="/bo-i-cancelada-och-estepona" locale="en" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
